import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { getCookie } from "../../utility/getCookie";
import {
  ShippingState,
  PincodeDeliverabilityRequest,
  PincodeDeliverabilityResponse,
  CourierOptionsRequest,
  CourierOptionsResponse,
  PdpDeliveryRequest,
  PdpDeliveryResponse,
  TrackingResponse,
} from "./types";

const API_URL =
  process.env.REACT_APP_SHIPPING_API_PREFIX ||
  `${process.env.REACT_APP_API_BASE_URL || ""}/api/v1/shipping`;

/** Shipping API wraps payloads as `{ success, data, timestamp }`. */
const unwrap = <T>(body: any): T =>
  (body && typeof body === "object" && "data" in body ? body.data : body) as T;

// Axios instance with cookies support
const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add Bearer token from cookies (if available)
api.interceptors.request.use(
  async (config) => {
    const token = getCookie("access_token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Async Thunks
export const checkPincodeDeliverability = createAsyncThunk(
  "shipping/checkPincodeDeliverability",
  async ({ customerPincode, sellerPincode }: PincodeDeliverabilityRequest, { rejectWithValue }) => {
    try {
      const response = await api.get(
        "/public/delivery-check/pincode-check",
        {
          params: { customerPincode, sellerPincode },
        }
      );
      return unwrap<PincodeDeliverabilityResponse>(response.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to check deliverability"
      );
    }
  }
);

export const checkPdpDelivery = createAsyncThunk(
  "shipping/checkPdpDelivery",
  async ({ sellerId, customerPincode }: PdpDeliveryRequest, { rejectWithValue }) => {
    try {
      const response = await api.get(
        "/public/delivery-check/pdp-delivery",
        { params: { sellerId, customerPincode } },
      );
      return unwrap<PdpDeliveryResponse>(response.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to check delivery",
      );
    }
  },
);

export const fetchCourierOptions = createAsyncThunk(
  "shipping/fetchCourierOptions",
  async (
    { sellerId, deliveryLatitude, deliveryLongitude, deliveryPincode }: CourierOptionsRequest,
    { rejectWithValue },
  ) => {
    try {
      const response = await api.get(
        "/public/delivery-check/courier-options",
        {
          params: { sellerId, deliveryLatitude, deliveryLongitude, deliveryPincode },
        },
      );
      return unwrap<CourierOptionsResponse>(response.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch courier options",
      );
    }
  },
);

export const trackByAwb = createAsyncThunk(
  "shipping/trackByAwb",
  async (awb: string, { rejectWithValue }) => {
    try {
      const response = await api.get<TrackingResponse>(
        `/shipments/by-awb/${awb}/tracking`,
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch tracking",
      );
    }
  },
);

const initialState: ShippingState = {
  deliverability: null,
  pdpDelivery: null,
  pdpDeliveryLoading: false,
  courierOptions: [],
  courierOptionsLoading: false,
  serviceable: null,
  tracking: null,
  trackingLoading: false,
  trackingError: null,
  loading: false,
  error: null,
  success: false,
};

const shippingSlice = createSlice({
  name: "shipping",
  initialState,
  reducers: {
    clearShippingState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.deliverability = null;
      state.pdpDelivery = null;
    },
    clearTracking: (state) => {
      state.tracking = null;
      state.trackingLoading = false;
      state.trackingError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkPincodeDeliverability.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(
        checkPincodeDeliverability.fulfilled,
        (state, action: PayloadAction<PincodeDeliverabilityResponse>) => {
          state.loading = false;
          state.success = true;
          state.deliverability = action.payload;
        }
      )
      .addCase(checkPincodeDeliverability.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.success = false;
      })
      .addCase(checkPdpDelivery.pending, (state) => {
        state.pdpDeliveryLoading = true;
        state.error = null;
      })
      .addCase(
        checkPdpDelivery.fulfilled,
        (state, action: PayloadAction<PdpDeliveryResponse>) => {
          state.pdpDeliveryLoading = false;
          state.pdpDelivery = action.payload;
          state.courierOptions = action.payload.hyperlocal?.options ?? [];
          state.serviceable = action.payload.hyperlocal?.serviceable ?? false;
        },
      )
      .addCase(checkPdpDelivery.rejected, (state, action) => {
        state.pdpDeliveryLoading = false;
        state.pdpDelivery = null;
        state.error = action.payload as string;
      })
      .addCase(fetchCourierOptions.pending, (state) => {
        state.courierOptionsLoading = true;
      })
      .addCase(
        fetchCourierOptions.fulfilled,
        (state, action: PayloadAction<CourierOptionsResponse>) => {
          state.courierOptionsLoading = false;
          state.courierOptions = action.payload.options || [];
          state.serviceable = action.payload.serviceable ?? false;
        },
      )
      .addCase(fetchCourierOptions.rejected, (state) => {
        state.courierOptionsLoading = false;
        state.courierOptions = [];
        state.serviceable = false;
      })
      .addCase(trackByAwb.pending, (state) => {
        state.trackingLoading = true;
        state.trackingError = null;
      })
      .addCase(
        trackByAwb.fulfilled,
        (state, action: PayloadAction<TrackingResponse>) => {
          state.trackingLoading = false;
          state.tracking = action.payload;
        },
      )
      .addCase(trackByAwb.rejected, (state, action) => {
        state.trackingLoading = false;
        state.tracking = null;
        state.trackingError = action.payload as string;
      });
  },
});

export const { clearShippingState, clearTracking } = shippingSlice.actions;
export default shippingSlice.reducer;
